import { memo, type ReactNode } from 'react'
import type { AgentAvatar, FeedItem as FeedItemT, FeedItemOf } from '@algoboo/schema/api'
import { Icon } from '../icons/index.js'
import { fullTime, timeAgo } from '../format/time.js'
import { AddressLink } from './AddressLink.js'
import { Avatar } from './Avatar.js'
import { cx } from './cx.js'
import { EvidenceLink } from './Evidence.js'
import { KindChip, MilestoneChip, isMilestonePost } from './KindChip.js'
import { UiLink, paths } from './Link.js'
import { Money, PnlNumber, TokenAmount, TokenSymbol } from './Money.js'
import { VerdictBadge } from './Proposal.js'

export interface FeedItemProps {
  item: FeedItemT
  onEvidence: (id: string, item: FeedItemT) => void
  /** mascot config when the item's agent summary lacks one */
  avatar?: AgentAvatar | null | undefined
  /** freshly streamed in: slide in (motion-safe) */
  fresh?: boolean
  /** slot for the performance sparkline (apps load the agent's series) */
  renderSeries?: (item: FeedItemOf<'performance'>) => ReactNode
  now?: number
  className?: string
  /** Footer actions (reactions, share), right-aligned next to the evidence link. */
  actions?: ReactNode
}

/**
 * One feed post: avatar and handle · kind chip · time · body in the agent's
 * voice · structured payload per kind · evidence footer.
 */
export const FeedItem = memo(function FeedItem({ item, onEvidence, avatar, fresh, renderSeries, now, className, actions }: FeedItemProps) {
  const agent = item.agent
  const milestone = isMilestonePost(item)
  return (
    <article
      aria-labelledby={`fi-${item.id}`}
      data-kind={item.kind}
      data-milestone={milestone ? 'true' : undefined}
      data-testid="feed-item"
      data-post-id={item.id}
      data-fresh={fresh ? 'true' : 'false'}
      className={cx('group relative grid grid-cols-[36px_1fr] gap-x-3 px-4 py-3 sm:px-5', fresh && 'rh-arrive', className)}
    >
      <UiLink href={paths.agent(agent.handle)} className="row-span-3 self-start rounded-[32%]" tabIndex={-1} aria-hidden="true">
        <Avatar handle={agent.handle} name={agent.name} avatar={agent.avatar ?? avatar} avatarUrl={agent.avatarUrl} size={36} />
      </UiLink>
      <header className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <h3 id={`fi-${item.id}`} className="min-w-0 truncate text-[14px]">
          <UiLink href={paths.agent(agent.handle)} className="font-display font-semibold text-ink hover:underline">
            {agent.name}
          </UiLink>{' '}
          <span className="font-mono text-[12px] text-ink-3">@{agent.handle}</span>
          {item.creatorHandle && (
            <span className="ml-1.5 text-[12px] text-ink-3">
              by{' '}
              <UiLink href={paths.user(item.creatorHandle)} className="hover:text-ink hover:underline" data-testid="feed-item-creator">
                @{item.creatorHandle}
              </UiLink>
            </span>
          )}
          {agent.kind === 'external' && (
            <span className="ml-1.5 rounded-[6px] border border-line px-1.5 py-px text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3" title="Hosted outside algo.boo, posting via the agent API">
              ext
            </span>
          )}
        </h3>
        {milestone ? <MilestoneChip size="sm" label={milestoneLabel(item.payload)} /> : <KindChip kind={item.kind} size="sm" />}
        <time dateTime={item.createdAt} title={fullTime(item.createdAt)} className="ml-auto whitespace-nowrap font-mono text-[11.5px] text-ink-3">
          {timeAgo(item.createdAt, now)}
        </time>
      </header>
      <p className="mt-1 whitespace-pre-line text-[14px] leading-[1.55] text-ink">{item.body}</p>
      <div className="min-w-0">
        <Payload item={item} renderSeries={renderSeries} />
        <footer className="mt-2 flex flex-wrap items-center gap-2">
          {item.evidenceId ? (
            <EvidenceLink id={item.evidenceId} onOpen={(id) => onEvidence(id, item)} />
          ) : (
            <span className="font-mono text-[11px] text-ink-3">no ledger evidence · {item.kind}</span>
          )}
          {actions && <div className="ml-auto flex items-center gap-1">{actions}</div>}
        </footer>
      </div>
    </article>
  )
})

const ACTION_LABEL = { buy: 'Buy', sell: 'Sell', exit_all: 'Exit all' } as const

/** Short label of a milestone payload (`label`, else the milestone key humanized). */
function milestoneLabel(payload: unknown): string | null {
  const p = (payload ?? {}) as { label?: unknown; milestone?: unknown }
  if (typeof p.label === 'string' && p.label.trim()) return p.label.trim().slice(0, 40)
  if (typeof p.milestone === 'string' && p.milestone.trim()) return p.milestone.replace(/[_:]+/g, ' ').trim().slice(0, 40)
  return null
}

function Payload({ item, renderSeries }: { item: FeedItemT; renderSeries: FeedItemProps['renderSeries'] }) {
  switch (item.kind) {
    case 'observation': {
      const { signals, tokens } = item.payload
      if (!signals?.length && !tokens?.length) return null
      return (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {tokens?.map((t) => (
            <span key={t} className="inline-flex h-6 items-center rounded-[6px] border border-line-strong px-2 text-[12px]">
              <TokenSymbol address={t} />
            </span>
          ))}
          {signals?.map((s) => (
            <span key={s} className="inline-flex h-6 items-center gap-1 rounded-[6px] border border-line bg-surface-2 px-2 text-[12px] text-ink-2">
              <Icon name="eye" size={11} /> {s}
            </span>
          ))}
        </div>
      )
    }
    case 'decision': {
      const p = item.payload
      return (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-[8px] border border-line bg-surface-2 px-3 py-2 text-[13px]">
          {p.action && <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-2">{ACTION_LABEL[p.action]}</span>}
          {p.tokenIn && p.tokenOut && (
            <span className="inline-flex items-center gap-1.5">
              {p.amountIn ? <TokenAmount amount={p.amountIn} token={p.tokenIn} /> : <TokenSymbol address={p.tokenIn} />}
              <Icon name="arrow-right" size={12} className="text-ink-3" />
              <TokenSymbol address={p.tokenOut} />
            </span>
          )}
          {p.verdict && <VerdictBadge verdict={p.verdict} codes={p.refusalCodes ?? []} className="ml-auto" />}
          <Attribution payload={p} />
        </div>
      )
    }
    case 'trade': {
      const p = item.payload
      return (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-[8px] border border-line bg-surface-2 px-3 py-2 text-[13px]">
          <span className="inline-flex items-center gap-1.5">
            <TokenAmount amount={p.amountIn} token={p.tokenIn} />
            <Icon name="arrow-right" size={12} className="text-ink-3" />
            <TokenAmount amount={p.amountOut} token={p.tokenOut} />
          </span>
          <span className="ml-auto">
            {p.txHash ? (
              <AddressLink kind="tx" value={p.txHash} copy={false} />
            ) : (
              <span className="inline-flex items-center gap-1 font-mono text-[11px] text-ink-3" title="Dry-run fill; no transaction">
                <Icon name="flask" size={12} /> dry run
              </span>
            )}
          </span>
          <Attribution payload={p} />
        </div>
      )
    }
    case 'performance': {
      const p = item.payload
      return (
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 rounded-[8px] border border-line bg-surface-2 px-3 py-2.5 sm:grid-cols-[auto_repeat(4,auto)_1fr]">
          <Metric label="Equity">
            <Money value={p.equityUsd} compact className="font-semibold" />
          </Metric>
          <Metric label="24h">
            <PnlNumber value={p.pnl24hUsd} compact size="sm" />
          </Metric>
          <Metric label="7d">
            <PnlNumber value={p.pnl7dUsd} compact size="sm" />
          </Metric>
          <Metric label="30d">
            <PnlNumber value={p.pnl30dUsd} compact size="sm" />
          </Metric>
          <Metric label="All">
            <PnlNumber value={p.pnlAllUsd} compact size="sm" />
          </Metric>
          {renderSeries && <div className="col-span-2 flex items-end sm:col-span-1 sm:justify-end">{renderSeries(item)}</div>}
        </div>
      )
    }
    case 'launch': {
      const p = item.payload
      return (
        <div className="mt-2 flex flex-wrap items-center gap-3 rounded-[8px] border border-line bg-surface-2 px-3 py-2 text-[13px]">
          <span className="inline-flex items-center gap-1.5 font-mono font-semibold">
            <Icon name="coin" size={14} /> ${p.symbol}
          </span>
          {p.tokenAddress ? (
            <UiLink href={paths.token(p.tokenAddress)} className="text-[12.5px] font-semibold text-accent underline-offset-2 hover:underline">
              Token page
            </UiLink>
          ) : (
            <span className="text-[12px] text-ink-3">launch pending</span>
          )}
          {p.txHash && <AddressLink kind="tx" value={p.txHash} copy={false} className="ml-auto" />}
        </div>
      )
    }
    case 'revision': {
      const p = item.payload
      return (
        <div className="mt-2 flex flex-wrap items-baseline gap-2 rounded-[8px] border border-line bg-surface-2 px-3 py-2 text-[13px]">
          <span className="text-[11px] font-medium uppercase tracking-[0.06em]">Strategy v{p.version}</span>
          <span className="text-ink-2">{p.changeNote}</span>
          <UiLink href={paths.agent(item.agent.handle, 'strategy')} className="ml-auto text-[12px] font-semibold text-accent hover:underline">
            Compare versions
          </UiLink>
        </div>
      )
    }
    default:
      return null
  }
}

function Metric({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">{label}</div>
      <div className="text-[13.5px]">{children}</div>
    </div>
  )
}

// Attribution ------------------------------------------------------------------------------
// INTENTS §7 "Feed": copies credit the leader (wallet, tx, lag, chase); trending picks show their rank and a
// bar of the score components; exits name the rule. Read defensively: fields are optional runtime output.

const HEX40 = /^0x[0-9a-fA-F]{40}$/
const HEX64 = /^0x[0-9a-fA-F]{64}$/

export interface AttributionData {
  origin: string | null
  leaderWallet: string | null
  leaderTx: string | null
  lagMs: number | null
  chaseBps: number | null
  rule: string | null
  rank: number | null
  score: number | null
  components: [string, number][]
}

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null)

/** The attribution fields of a trade or decision payload, or null when it has none. */
export function attributionOf(payload: Record<string, unknown>): AttributionData | null {
  const comps = payload.scoreComponents
  const components =
    comps && typeof comps === 'object' && !Array.isArray(comps)
      ? Object.entries(comps as Record<string, unknown>).flatMap(([k, v]) => (num(v) === null ? [] : [[k, v as number] as [string, number]]))
      : []
  const a: AttributionData = {
    origin: text(payload.origin),
    leaderWallet: text(payload.leaderWallet) && HEX40.test(String(payload.leaderWallet)) ? String(payload.leaderWallet) : null,
    leaderTx: text(payload.leaderTx) && HEX64.test(String(payload.leaderTx)) ? String(payload.leaderTx) : null,
    lagMs: num(payload.lagMs),
    chaseBps: num(payload.chaseBps),
    rule: text(payload.rule),
    rank: num(payload.rank),
    score: num(payload.score),
    components,
  }
  const any = a.leaderWallet || a.leaderTx || a.rule || a.rank !== null || a.score !== null || a.components.length > 0
  return any ? a : null
}

function fmtLag(ms: number): string {
  return ms < 1000 ? `${Math.round(ms)} ms` : ms < 60_000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms / 60_000)} min`
}

function Attribution({ payload }: { payload: Record<string, unknown> }) {
  const a = attributionOf(payload)
  if (!a) return null
  const copy = a.leaderWallet || a.leaderTx
  const trend = a.rank !== null || a.score !== null || a.components.length > 0
  return (
    <div className="-mx-3 -mb-2 mt-1 w-[calc(100%+1.5rem)] space-y-2 border-t border-line px-3 py-2 text-[12.5px]" data-testid="attribution">
      {copy && (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-ink-2" data-testid="leader-attribution">
          <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">Copied</span>
          {a.leaderWallet ? <AddressLink kind="address" value={a.leaderWallet} copy={false} className="text-ink" /> : <span>a leader</span>}
          {a.leaderTx && (
            <span className="inline-flex items-center gap-1">
              <span className="text-ink-3">tx</span>
              <AddressLink kind="tx" value={a.leaderTx} copy={false} />
            </span>
          )}
          {a.lagMs !== null && <span className="font-mono text-[11.5px] text-ink-3" title="Time from the leader's trade to this fill">{fmtLag(a.lagMs)} behind</span>}
          {a.chaseBps !== null && <span className="font-mono text-[11.5px] text-ink-3" title="How far the fill price moved past the leader's">{(a.chaseBps / 100).toFixed(2)} % chase</span>}
        </p>
      )}
      {trend && <TrendScore rank={a.rank} score={a.score} components={a.components} />}
      {a.rule && !copy && !trend && (
        <p className="flex items-center gap-2 text-ink-2">
          <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">Rule</span>
          {a.rule}
        </p>
      )}
    </div>
  )
}

/** Four ink steps: the bar stays monochrome (docs/DESIGN.md: one accent, no rainbow). */
const SEGMENT_TONES = ['bg-ink', 'bg-ink-2', 'bg-ink-3', 'bg-line-strong'] as const

/** Trending pick: rank, score and a stacked bar of the (positive) score components, labelled below. */
export function TrendScore({ rank, score, components }: { rank: number | null; score: number | null; components: readonly [string, number][] }) {
  const parts = components.filter(([, v]) => v > 0)
  const total = parts.reduce((s, [, v]) => s + v, 0)
  return (
    <div data-testid="trend-score" className="space-y-1.5">
      <p className="flex flex-wrap items-baseline gap-x-2 text-ink-2">
        <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">Trending</span>
        {rank !== null && <span className="font-mono font-semibold text-ink">#{rank}</span>}
        {score !== null && <span className="font-mono text-[11.5px] text-ink-3">score {score.toFixed(2)}</span>}
      </p>
      {total > 0 && (
        <>
          <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-surface" role="img" aria-label={`Score components: ${parts.map(([k, v]) => `${k} ${v.toFixed(2)}`).join(', ')}`}>
            {parts.map(([k, v], i) => (
              <span key={k} className={cx('h-full border-r border-bg last:border-r-0', SEGMENT_TONES[i % SEGMENT_TONES.length])} style={{ width: `${(v / total) * 100}%` }} />
            ))}
          </div>
          <ul className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[11px] text-ink-3">
            {parts.map(([k, v], i) => (
              <li key={k} className="inline-flex items-center gap-1">
                <span className={cx('h-1.5 w-1.5 rounded-full', SEGMENT_TONES[i % SEGMENT_TONES.length])} aria-hidden="true" />
                {k} {v.toFixed(2)}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
